import React, { useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../../components/ui/Dialog";
import { useUpdateLeaveAllocation } from "../../../../api/queries/leaveQueries";
import {
  getApiErrorMessage,
  getApiErrorStatus,
} from "../../../../utils/apiError";
import { formatPlainDate } from "../../../../utils/dateTime";
import { formatDays } from "../review/leaveFormat";
import { allocationName, cycleContaining } from "./allocationFormat";

const MAX_DAYS = 365;
const REASON_MAX = 500;

/**
 * Adjust (or create) one employee's allocation for one policy and anniversary
 * cycle. New cycles are pre-filled with the policy's entitlement. The minimum
 * is what's already used + pending. 400 / 409 stay in the dialog; 403 / 404
 * close it with a toast.
 */
const AllocationDialog = ({
  employee,
  leaveType,
  balance,
  periodStart,
  onClose,
}) => {
  const policy = leaveType.policy || null;
  const used = balance?.used ?? 0;
  const pending = balance?.pending ?? 0;
  const min = Math.round((used + pending) * 10) / 10;
  const current = balance ? balance.allocated : null;
  const cycleStart = balance?.periodStart || periodStart;
  const cycleEnd =
    balance?.periodEnd ||
    (cycleStart ? cycleContaining(employee.hiringDate, cycleStart)?.end : null);

  const [total, setTotal] = useState(
    current === null
      ? String(policy?.annualEntitlement ?? "")
      : String(current),
  );
  const [reason, setReason] = useState("");
  const [fieldError, setFieldError] = useState(null);
  const save = useUpdateLeaveAllocation();

  const value = total === "" ? null : Number(total);
  const problem =
    value === null || Number.isNaN(value)
      ? "Enter the total days."
      : value < min
        ? `At least ${min} (already used or pending).`
        : value > MAX_DAYS
          ? `At most ${MAX_DAYS}.`
          : (value * 2) % 1 !== 0
            ? "Use steps of 0.5."
            : null;
  const unchanged = current !== null && value === current;
  const available =
    value === null || Number.isNaN(value)
      ? null
      : Math.round((value - used - pending) * 10) / 10;
  const policyName = policy?.name || leaveType.name;

  const submit = (e) => {
    e.preventDefault();
    if (problem || unchanged) return;
    setFieldError(null);
    save.mutate(
      {
        employeeId: employee.id,
        leaveTypeId: leaveType.id,
        periodStart: cycleStart || undefined, // none = the cycle containing today
        allocatedDays: value,
        reason,
      },
      {
        onSuccess: (res) => {
          toast.success(
            res?.changed === false
              ? "No change"
              : current === null
                ? "Cycle created"
                : "Allocation saved",
          );
          onClose();
        },
        onError: (err) => {
          const status = getApiErrorStatus(err);
          const msg = err?.response?.data?.message;
          const text = Array.isArray(msg)
            ? msg.join(" ")
            : getApiErrorMessage(err, "Could not save the allocation.");
          if (status === 403 || status === 404) {
            toast.error(text);
            onClose();
          } else {
            setFieldError(text); // 400 (eligibility, no policy, cycle start, validation) / 409 below the minimum
          }
        },
      },
    );
  };

  return (
    <Dialog
      title={policyName}
      subtitle={`${allocationName(employee)} · ${employee.employeeId}`}
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button
            type="button"
            className="btn btn-outline-secondary profile-btn"
            onClick={onClose}
            disabled={save.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="allocation-form"
            className="btn btn-primary profile-btn"
            disabled={save.isPending || !!problem || unchanged}
          >
            {save.isPending && (
              <span
                className="spinner-border spinner-border-sm"
                aria-hidden="true"
              ></span>
            )}
            {current === null ? "Create cycle" : "Save"}
          </button>
        </>
      }
    >
      <form id="allocation-form" onSubmit={submit} noValidate>
        <div className="alloc-cycle-banner">
          <i className="bi bi-calendar-range"></i>
          <div>
            <div className="fw-semibold">
              {cycleStart
                ? `Cycle ${formatPlainDate(cycleStart)} – ${cycleEnd ? formatPlainDate(cycleEnd) : "…"}`
                : "Current cycle"}
            </div>
            {policy && (
              <div className="fs-075 text-slate-600">
                Policy entitlement: {formatDays(policy.annualEntitlement)} per
                cycle
                {policy.minYearsOfService
                  ? ` · from ${policy.minYearsOfService} year(s) of service`
                  : ""}
              </div>
            )}
          </div>
        </div>

        <div className="alloc-facts">
          <div>
            <div className="approval-meta-label">Used</div>
            <div className="alloc-fact-value">{used}</div>
          </div>
          <div>
            <div className="approval-meta-label">Pending</div>
            <div className="alloc-fact-value">{pending}</div>
          </div>
          <div>
            <div className="approval-meta-label">Current allocation</div>
            <div className="alloc-fact-value">
              {current === null ? (
                <span className="text-slate-400 fs-085">Not created yet</span>
              ) : (
                current
              )}
            </div>
          </div>
        </div>

        <label htmlFor="alloc-total" className="profile-label">
          Total days for this cycle <span className="text-danger">*</span>
        </label>
        <input
          id="alloc-total"
          type="number"
          className={`form-control ${fieldError || (total !== "" && problem) ? "is-invalid" : ""}`}
          step="0.5"
          min={min}
          max={MAX_DAYS}
          value={total}
          onChange={(e) => {
            setTotal(e.target.value);
            setFieldError(null);
          }}
          autoFocus
          aria-describedby="alloc-total-help"
        />
        <div
          id="alloc-total-help"
          className={`fs-075 mt-1 ${fieldError || (total !== "" && problem) ? "text-danger" : "text-slate-500"}`}
        >
          {fieldError ||
            (total !== "" && problem) ||
            (current === null && policy
              ? `Pre-filled with the policy entitlement. Minimum ${min}.`
              : `Minimum ${min} (already used or pending).`)}
        </div>

        <label htmlFor="alloc-reason" className="profile-label mt-3">
          Reason <span className="text-slate-400 fw-normal">(optional)</span>
        </label>
        <textarea
          id="alloc-reason"
          className="form-control"
          rows={2}
          maxLength={REASON_MAX}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Adjusted per HR memo"
        />
        <div className="text-end text-slate-400 fs-070 mt-1">
          {reason.length}/{REASON_MAX}
        </div>

        {available !== null && !problem && (
          <p className={`fs-085 mb-0 ${available < 0 ? "text-danger" : ""}`}>
            Available after saving: <strong>{formatDays(available)}</strong>
            {unchanged && <span className="text-slate-500"> (no change)</span>}
          </p>
        )}
      </form>
    </Dialog>
  );
};

export default AllocationDialog;
